import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BoostYearlyRevenueQuery, BoostYearlyRevenueQueryVariables, Menu } from 'generated/graphql';
import { AppsList } from 'components/AppsList/AppsList';
import { SelectMenu } from 'components/SelectMenu';
import AvailabilityPill from 'components/AvailabilityPill/AvailabilityPill';
import SidePanel from 'components/SidePanel/SidePanel';
import DetailsFooter from 'components/SingleItem/DetailsFooter';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { PublishMenuMutation, UpdateMenuLinkMutation } from 'queries/Menu';
import { useModalContext } from 'common/Context/ModalContext';
import { SnackBarTypeEnum } from 'types';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import { type Option } from 'types';
import { BOOST_YEARLY_REVENUE } from 'queries/Venue';
import { groupByBrand, type TileSection } from 'components/Tiles/tileUtils';
import styles from './Surcharge.module.scss';

interface Props {
  menus?: Menu[] | null;
  venueId?: string;
  venueName?: string;
  incrementMenuSurchargePublishCount?: () => void;
}

const VIRTUAL_BRAND_IDENTIFIER = 'virtual';
const SURCHARGE_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];

enum Align {
  Left = 'left',
  Right = 'right',
  Center = 'center'
}

enum Size {
  Small = 'small',
  Large = 'large'
}

enum TextAlignment {
  Left = 'left',
  Center = 'center',
  Right = 'right'
}

const selectMenuProps = {
  buttonStyle: styles.Button,
  align: Align.Right,
  size: Size.Small,
  height: 263,
  width: 258,
  textAlignment: TextAlignment.Left
};

const getLabelForValue = (value: number): string => {
  if (value === 0) {
    return `Standard price`;
  }

  return `Increase by ${value}%`;
};

const selectableOptions: Option[] = SURCHARGE_OPTIONS.map((option) => {
  const value = getLabelForValue(option);
  return {
    content: <span>{value}</span>,
    value,
    type: 'button'
  };
});

const Surcharge: React.FC<Props> = ({ menus, venueId, venueName, incrementMenuSurchargePublishCount }) => {
  const [originalSurchargeValue, setOriginalSurchargeValue] = useState<{ [key: string]: number }>({});
  const [surcharge, setSurcharge] = useState<{ [key: string]: number }>({});
  const [loading, setLoading] = useState(false);
  const [updatedAppNames, setUpdatedAppNames] = useState<string[]>([]);
  const location = useLocation();
  const { openSnackBar } = useModalContext();
  const navigate = useNavigate();
  const [updateMenuLinkMutation] = useMutation(UpdateMenuLinkMutation);
  const [publishMenu] = useMutation(PublishMenuMutation);
  const { SURCHARGE_WHITELISTED_APP_IDS } = getEnvironmentVariables();

  // Filter whitelisted apps
  const availableMenus: Menu[] = useMemo(() => {
    if (!menus || !SURCHARGE_WHITELISTED_APP_IDS) {
      return [];
    }

    return menus.filter((menu) => {
      if (menu.neverPublished) {
        return false;
      }

      const app = menu.apps[0];

      if (SURCHARGE_WHITELISTED_APP_IDS.includes(Number(app.id))) {
        return true;
      }

      return SURCHARGE_WHITELISTED_APP_IDS.includes(Number(app.id.split('.')[1]));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menus]);

  const nonSupportedAppMenus = menus?.filter(
    (menu) =>
      !(
        SURCHARGE_WHITELISTED_APP_IDS?.includes(Number(menu.apps[0].id)) ||
        SURCHARGE_WHITELISTED_APP_IDS?.includes(Number(menu.apps[0].id.split('.')[1]))
      )
  );

  const groupedMenus: TileSection[] = useMemo(() => {
    if (!availableMenus) {
      return [];
    }
    return groupByBrand(availableMenus, venueName ?? 'Menu');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMenus]);

  const closeModal = (): void => {
    const { pathname } = location;
    const pathnameWithoutHash = pathname.split(`/pricing`)[0];

    navigate(pathnameWithoutHash);
  };

  const handleSelectChange = (appId: string, value: number): void => {
    setSurcharge((prevSurcharge) => {
      const newSurcharge = {
        ...prevSurcharge,
        [appId]: value
      };
      return newSurcharge;
    });
  };

  const {
    data: yearlyRevenueData,
    loading: yearlyRevenueLoading,
    error: yearlyRevenueError
  } = useQuery<BoostYearlyRevenueQuery, BoostYearlyRevenueQueryVariables>(BOOST_YEARLY_REVENUE, {
    variables: {
      locationId: venueId || ''
    }
  });

  const formattedSurgeRevenue = useMemo(() => {
    if (yearlyRevenueLoading || yearlyRevenueError || !yearlyRevenueData || !yearlyRevenueData.boostYearlyRevenue) {
      return undefined;
    }

    const formattedRevenue = yearlyRevenueData.boostYearlyRevenue.toLocaleString('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    return formattedRevenue;
  }, [yearlyRevenueData, yearlyRevenueLoading, yearlyRevenueError]);

  // Update with the original surcharge values
  const setOriginalSurchargeValues = useCallback(() => {
    let newSurcharge: { [key: string]: number } = {};
    availableMenus.forEach((menu) => {
      newSurcharge = {
        ...newSurcharge,
        [menu.apps[0].id]: menu.surcharge?.amount ?? 0
      };
      return newSurcharge;
    });
    setOriginalSurchargeValue(newSurcharge);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMenus]);

  useEffect(setOriginalSurchargeValues, [setOriginalSurchargeValues]);

  const displaySnackBar = (message: string, type: SnackBarTypeEnum): void => {
    openSnackBar(message, undefined, undefined, type, 10000);
  };

  // Update the menu with the surcharge value
  const updateMenuWithSurcharge = async (appId: string, surcharge: number, isLastIndex?: boolean): Promise<void> => {
    try {
      await updateMenuLinkMutation({
        variables: {
          venueId,
          appId,
          surcharge: {
            amount: surcharge,
            type: 'percentage'
          },
          skipFetch: !isLastIndex
        }
      });
      await publishMenu({
        variables: {
          venueId,
          appId
        },
        onError: () => {
          throw new Error(`Failed to update menu for appId: ${appId}`);
        }
      });
      if (incrementMenuSurchargePublishCount) {
        incrementMenuSurchargePublishCount();
      }
    } catch (error) {
      throw new Error(`Failed to update menu for appId: ${appId}`);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    const surchargeEntries = Object.entries(surcharge);
    if (surchargeEntries.length === 0) {
      setLoading(false);
      return;
    }

    const updateMenuPromises: (() => Promise<void>)[] = [];
    surchargeEntries.forEach(([appId, surchargeValue], i) => {
      const isLastIndex = i === (surchargeEntries.length ?? 0) - 1;
      updateMenuPromises.push(() => updateMenuWithSurcharge(appId, surchargeValue, isLastIndex));
    });

    if (updateMenuPromises.length === 0) {
      setLoading(false);
      return;
    }

    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const updateMenuPromise of updateMenuPromises) {
        // awaiting each updateMenuPromise to ensure the menu is updated before moving to the next and then fetching the menus on the last iteration
        // eslint-disable-next-line no-await-in-loop
        await updateMenuPromise();
      }

      displaySnackBar('Updated! Please allow a few minutes for your apps to reflect.', SnackBarTypeEnum.SUCCESS);
    } catch (error) {
      displaySnackBar('An error occurred while updating your menus. Please try again later.', SnackBarTypeEnum.INFO);
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  const renderAppsSection = (menu: TileSection, index: number): React.ReactNode => {
    const apps = menu.menus.flatMap((menu) => menu.apps);
    const singleSection = groupedMenus.length === 1;
    const lastIndex = index === groupedMenus.length - 1;
    return (
      <div>
        {availableMenus.find((menu) => menu.apps[0].id.includes(VIRTUAL_BRAND_IDENTIFIER)) && (
          <div className={styles.TopSection}>
            <span>{menu.name}</span>
          </div>
        )}
        <div className={styles.Section} style={{ marginBottom: singleSection ? '0' : '2rem' }}>
          <ul className={styles.OptionList} data-testid="option-list">
            <li className={styles.HeadingRow}>
              {apps && <AppsList apps={apps} />}
              <span className={styles.AllApps}>All apps</span>
              <div className={styles.SelectMenu}>
                <SelectMenu
                  options={selectableOptions}
                  defaultButtonContent={
                    <AvailabilityPill
                      availability={
                        surcharge[apps[0].id] !== undefined
                          ? getLabelForValue(surcharge[apps[0].id])
                          : getLabelForValue(originalSurchargeValue[apps[0].id])
                      }
                      background="#EBF3FF"
                      color="#0056BB"
                    />
                  }
                  downIcon={null}
                  onChange={(value) => {
                    const selectedOption = SURCHARGE_OPTIONS.find((option) => getLabelForValue(option) === value);
                    if (selectedOption !== undefined) {
                      apps?.forEach((app) => handleSelectChange(app.id, selectedOption));
                      const appNames = apps.map((app) =>
                        app.id.includes('virtual') && app.brand ? `${app.name} - brand` : app.name
                      );
                      setUpdatedAppNames([
                        ...updatedAppNames,
                        ...appNames.filter((appName) => !updatedAppNames.includes(appName))
                      ]);
                      logAmplitudeEvent({
                        name: `set ${selectedOption === 0 ? `standard price` : 'increase'} for all apps`,
                        category: AmplitudeEventCategories.MM_SIDE_PANEL_PRICING,
                        properties: {
                          [AmplitudeEventProperties.APPS]: appNames.join(', '),
                          ...(selectedOption !== 0 && {
                            [AmplitudeEventProperties.INCREMENT]: `${getLabelForValue(selectedOption).split('by ')[1]}`
                          })
                        }
                      });
                    }
                  }}
                  {...selectMenuProps}
                />
              </div>
            </li>
          </ul>
        </div>
        {formattedSurgeRevenue && lastIndex ? (
          <div className={singleSection ? styles.SurgeContainer : ''}>
            <div className={styles.SurgeValue} data-testid="surge-value">
              <i className="mm-currency" />
              <p>
                You&apos;ve made <span>{formattedSurgeRevenue} in returns</span> since using Dynamic pricing.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={styles.Surcharge}>
      <SidePanel
        title="Dynamic pricing"
        description="Use this feature to temporarily increase your app menu prices on weekends, public holidays or managing everyday prices strategically. Note: Editing product prices and adding options will be disabled during a dynamic price increase."
        route="pricing"
      >
        <div className={styles.Container}>
          <div className={styles.Alert} data-testid="surcharge-alert">
            <i className="mm-error-circle" />
            <span>Dynamic pricing will stay active until &apos;Standard price&apos; is selected.</span>
          </div>
          <div className={styles.AppsSection}>
            {groupedMenus.map((menu, index) => renderAppsSection(menu, index))}
            {nonSupportedAppMenus?.length ? (
              <div className={styles.UnsupportedApps} data-testid="unsupported-apps">
                {nonSupportedAppMenus.map(({ apps }) => (
                  <AppsList apps={apps} key={apps[0].id} />
                ))}
                <span className={styles.NotSupported}>
                  <i className="mm-error-circle" /> App{nonSupportedAppMenus.length > 1 ? `s don't` : ` doesn't`}{' '}
                  support dynamic pricing
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <div className={styles.Footer}>
          <DetailsFooter
            closeModal={() => {
              closeModal();
              logAmplitudeEvent({
                name: 'click cancel',
                category: AmplitudeEventCategories.MM_SIDE_PANEL_PRICING
              });
            }}
            handleConfirm={() => {
              setLoading(true);
              handleConfirm();
              logAmplitudeEvent({
                name: 'click update',
                category: AmplitudeEventCategories.MM_SIDE_PANEL_PRICING,
                properties: {
                  enabled: `${Object.values(surcharge).some((value) => value !== 0)}`,
                  ...(updatedAppNames.length && {
                    apps: `${updatedAppNames.join(', ')}`
                  })
                }
              });
            }}
            confirmButton="Update"
            loading={loading}
          />
        </div>
      </SidePanel>
    </div>
  );
};

export default Surcharge;
